import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtMYR, fmtL, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/DateInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Loader2, HandCoins, Fuel } from "lucide-react";

const inp = "mt-1.5 bg-[#0B0F17] border-[#374151] text-white";
const PRODUCTS = ["RON95", "DIESEL"];

export function CreditTab({ station, date, pumps }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [payType, setPayType] = useState("credit"); // credit (fuel sale) | cash (prepayment)
  const [product, setProduct] = useState("RON95");
  const [liters, setLiters] = useState("");
  const [price, setPrice] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [txnDate, setTxnDate] = useState(date);
  const [saving, setSaving] = useState(false);
  const [txns, setTxns] = useState([]);

  const loadCustomers = useCallback(() => {
    api.get("/credit/customers").then((r) => {
      setCustomers(r.data);
      if (!customerId && r.data[0]) setCustomerId(r.data[0].id);
    }).catch(() => {});
  }, [customerId]);

  const loadTxns = useCallback(() => {
    if (!station) return;
    api.get(`/credit/transactions?station_id=${station}`).then((r) => setTxns(r.data)).catch(() => setTxns([]));
  }, [station]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { loadTxns(); }, [loadTxns]);
  useEffect(() => { setTxnDate(date); }, [date]);

  // auto-fill pump price from station pumps for the chosen product
  useEffect(() => {
    if (payType !== "credit") return;
    const p = (pumps || []).find((x) => (x.product || "").toUpperCase() === product);
    if (p && (price === "" || price === undefined)) setPrice(String(p.retail_price ?? ""));
  }, [product, pumps, payType]); // eslint-disable-line

  const computedAmount = payType === "credit"
    ? (amount !== "" ? Number(amount) : (Number(liters || 0) * Number(price || 0)))
    : Number(amount || 0);

  const selected = customers.find((c) => c.id === customerId);

  const submit = async () => {
    if (!station) return toast.error("Pick a station");
    if (!customerId) return toast.error("Pick a credit customer");
    if (!txnDate) return toast.error("Pick a date");
    const body = { customer_id: customerId, station_id: station, date: txnDate, notes };
    if (payType === "credit") {
      if (!Number(liters) && !Number(amount)) return toast.error("Enter litres (or an amount)");
      body.type = "sale"; body.product = product; body.liters = Number(liters || 0);
      body.pump_price = Number(price || 0); body.amount = amount !== "" ? Number(amount) : null;
    } else {
      if (!Number(amount)) return toast.error("Enter the pre-payment amount");
      body.type = "prepayment"; body.amount = Number(amount);
    }
    setSaving(true);
    try {
      await api.post("/credit/transactions", body);
      toast.success(payType === "credit" ? "Credit sale recorded" : "Pre-payment recorded");
      setLiters(""); setAmount(""); setNotes("");
      loadCustomers(); loadTxns();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSaving(false);
  };

  const del = async (id) => {
    try { await api.delete(`/credit/transactions/${id}`); toast.success("Deleted"); loadCustomers(); loadTxns(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  if (!customers.length) {
    return <div className="text-sm text-[#9CA3AF] py-8 text-center" data-testid="credit-tab-empty">
      No credit customers yet. Add one on the <b className="text-white">Credit Customers</b> page, then record sales here.
    </div>;
  }

  return (
    <div className="space-y-5" data-testid="credit-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2">
          <Label className="text-[#9CA3AF] text-xs">Credit customer</Label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger data-testid="credit-tab-customer" className={inp}><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-3">
          <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Current balance</div>
          <div className={`metric-value text-lg font-semibold mt-0.5 ${selected && selected.balance < 0 ? "text-red-400" : "text-emerald-400"}`} data-testid="credit-tab-balance">
            {selected ? `${fmtMYR(selected.balance)}${selected.balance < 0 ? " owed" : " credit"}` : "—"}
          </div>
        </div>
      </div>

      <div className="max-w-xs" data-testid="credit-tab-date-wrap">
        <Label className="text-[#9CA3AF] text-xs">{payType === "cash" ? "Date deposit was made" : "Date of sale"}</Label>
        <DateInput data-testid="credit-tab-date" value={txnDate} onChange={setTxnDate} className={inp} />
      </div>

      <div className="inline-flex rounded-lg border border-[#1F2937] p-1 bg-[#0B0F17]" data-testid="credit-tab-paytype">
        <button onClick={() => setPayType("credit")} data-testid="credit-tab-type-credit"
          className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${payType === "credit" ? "bg-amber-500 text-black" : "text-[#9CA3AF] hover:text-white"}`}>
          <Fuel className="w-4 h-4" /> Credit fuel sale
        </button>
        <button onClick={() => setPayType("cash")} data-testid="credit-tab-type-cash"
          className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${payType === "cash" ? "bg-emerald-500 text-black" : "text-[#9CA3AF] hover:text-white"}`}>
          <HandCoins className="w-4 h-4" /> Pre-payment (cash in)
        </button>
      </div>

      {payType === "credit" ? (
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div><Label className="text-[#9CA3AF] text-xs">Product</Label>
            <Select value={product} onValueChange={(v) => { setProduct(v); setPrice(""); }}>
              <SelectTrigger data-testid="credit-tab-product" className={inp}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">{PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-[#9CA3AF] text-xs">Litres</Label><Input type="number" step="0.01" data-testid="credit-tab-liters" value={liters} onChange={(e) => setLiters(e.target.value)} className={inp} /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Pump price</Label><Input type="number" step="0.001" data-testid="credit-tab-price" value={price} onChange={(e) => setPrice(e.target.value)} className={inp} /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Amount (RM)</Label><Input type="number" step="0.01" data-testid="credit-tab-amount" placeholder={computedAmount ? computedAmount.toFixed(2) : "auto"} value={amount} onChange={(e) => setAmount(e.target.value)} className={inp} /></div>
          <Button onClick={submit} disabled={saving} data-testid="credit-tab-submit" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Charge on credit</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div><Label className="text-[#9CA3AF] text-xs">Pre-payment amount (RM)</Label><Input type="number" step="0.01" data-testid="credit-tab-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className={inp} /></div>
          <div><Label className="text-[#9CA3AF] text-xs">Notes</Label><Input data-testid="credit-tab-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} /></div>
          <Button onClick={submit} disabled={saving} data-testid="credit-tab-submit" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Record pre-payment</Button>
        </div>
      )}

      <div className="pt-2">
        <div className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">Recent credit activity · this station</div>
        {txns.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="credit-tab-table">
              <thead><tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono border-b border-[#1F2937]">
                {["Date", "Customer", "Type", "Product", "Litres", "Amount", ""].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-b border-[#1F2937]/60" data-testid={`credit-tab-row-${t.id}`}>
                    <td className="py-2 pr-3 metric-value text-white/80">{fmtDate(t.date)}</td>
                    <td className="py-2 pr-3">{t.customer_name}</td>
                    <td className="py-2 pr-3">{t.type === "prepayment" ? <span className="text-emerald-400">Pre-payment</span> : <span className="text-amber-400">Fuel sale</span>}</td>
                    <td className="py-2 pr-3">{t.product || "—"}</td>
                    <td className="py-2 pr-3 metric-value">{t.type === "sale" ? fmtL(t.liters || 0) : "—"}</td>
                    <td className={`py-2 pr-3 metric-value ${t.type === "prepayment" ? "text-emerald-400" : "text-amber-400"}`}>{t.type === "prepayment" ? "+" : "−"}{fmtMYR(t.amount)}</td>
                    <td className="py-2 pr-2 text-right"><button onClick={() => del(t.id)} data-testid={`credit-tab-delete-${t.id}`} className="text-[#9CA3AF] hover:text-red-400"><Trash2 className="w-3.5 h-3.5 inline" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="text-sm text-[#9CA3AF] py-6 text-center">No credit activity recorded at this station yet.</div>}
      </div>
    </div>
  );
}
